import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store, createSelector } from '@ngrx/store';
import { BreadcrumbOption, ButtonComponentConfig, ButtonSize, HeadingComponentConfig } from '@rappider/rappider-components/utils';
import { defaultToolbarTitleHeadingSize, PATH_DEFINITIONS, QUERY_PARAM_DEFINITIONS } from '@rappider/shared/definitions';
import { DeleteUIDataEvent, UpdateUIDataEvent } from 'libs/project/src/lib/states/ui-data-event-state/ui-data-event.actions';
import { Subscription, combineLatest, interval, Subject, takeUntil, BehaviorSubject } from 'rxjs';
import { ProjectWithRelations, UiDataEventWithRelations, UiDataStoreWithRelations } from '@rappider/rappider-sdk';
import { DATA_EVENT_LIST_CONFIG } from '../../../data-events/components/data-event-list/config/data-event-list-config';
import { RouterStateService, StoreKeys, SyncRemoteDataService } from '@rappider/services';
import { cloneDeep, orderBy } from 'lodash';
import { DataSchemaCategory } from '../../../data-schema/models/data-schema-category.enum';
import { UI_DATA_STORE_DETAIL_LIST_CREATE_CONFIG, UI_DATA_STORE_DETAIL_LIST_UPDATE_CONFIG } from '../ui-data-store-list/config/ui-data-store-detail-list-config';
import { UI_STEP_FUNCTION_LIST_CONFIG } from '@rappider/shared/configs';
import { UI_DATA_UPDATE_FUNCTION_LIST_CONFIG } from '../../../ui-data-update-function/components/ui-data-update-function-list/config/ui-data-update-function-list-config';
import { getDataSchemasWithDetailsSelector } from 'libs/project/src/lib/states/selectors/get-data-schemas-with-details.selector';

@Component({
  selector: 'rappider-ui-data-store-detail',
  templateUrl: './ui-data-store-detail.component.html',
  styleUrls: ['./ui-data-store-detail.component.scss']
})
export class UiDataStoreDetailComponent implements OnInit, OnDestroy {
  // Configuration constants
  DATA_EVENT_LIST_CONFIG = DATA_EVENT_LIST_CONFIG;
  readonly UI_DATA_STORE_DETAIL_LIST_CREATE_CONFIG = UI_DATA_STORE_DETAIL_LIST_CREATE_CONFIG;
  readonly UI_DATA_STORE_DETAIL_LIST_UPDATE_CONFIG = UI_DATA_STORE_DETAIL_LIST_UPDATE_CONFIG;
  readonly QUERY_PARAM_DEFINITIONS = QUERY_PARAM_DEFINITIONS;
  UI_STEP_FUNCTION_LIST_CONFIG = UI_STEP_FUNCTION_LIST_CONFIG;
  UI_DATA_UPDATE_FUNCTION_LIST_CONFIG = UI_DATA_UPDATE_FUNCTION_LIST_CONFIG;

  // Main title configuration
  readonly mainTitle: HeadingComponentConfig = {
    content: 'PROJECT_MODULE.UI_DATA_STORE_DETAIL_COMPONENT.UI_DATA_STORE_DETAIL',
    type: defaultToolbarTitleHeadingSize
  };

  // Close button configuration
  readonly closeButton: ButtonComponentConfig = {
    icon: { name: 'fa-solid fa-xmark' },
    size: ButtonSize.ExtraSmall
  };

  // Component properties
  title: BreadcrumbOption[];
  uiDataStoreId: string;
  uiDataStore: UiDataStoreWithRelations;
  activeProject: ProjectWithRelations;
  uiDataEvents: UiDataEventWithRelations[];

  // Loading states
  uiDataEventsLoading = false;
  uiDataStoresLoading = false;
  uiStepFunctionsLoading = false;
  isFieldsLoading = false;
  dataSchemaLoading = false;

  // UI states
  displayToolbar = false;
  displayToolbarBackButton = false;

  // Field management
  notCreatedFields: any[] = [];
  notUpdatedFields: any[] = [];
  notCreatedFieldsVisible = false;
  notUpdatedFieldsVisible = false;

  // Private properties
  private subscriptions: Subscription[] = [];
  private pollingDataStore$ = new Subject<void>();
  private componentDestroyed$ = new Subject<void>();

  constructor(
    private activatedRoute: ActivatedRoute,
    private store: Store<any>,
    private router: Router,
    private routerStateService: RouterStateService,
    private syncRemoteDataService: SyncRemoteDataService
  ) { }

  ngOnInit(): void {
    this.initializeComponent();
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  // Event handlers
  onUIDataEventDelete(uiDataEvent: UiDataEventWithRelations): void {
    this.store.dispatch(new DeleteUIDataEvent({ uiDataEvent }));
  }

  onUIDataEventEdit(uiDataEvent: UiDataEventWithRelations): void {
    this.store.dispatch(new UpdateUIDataEvent({
      uiDataEventId: uiDataEvent.id,
      uiDataEvent,
      navigateAfterUpdate: true
    }));
  }

  navigateCreateDataEventPage(dataStoreId: string): void {
    this.routerStateService.navigate(
      `${PATH_DEFINITIONS.PROJECTS.DATA_EVENT_CREATE}/${dataStoreId}`
    );
  }

  closeNotCreatedFields(): void {
    this.notCreatedFieldsVisible = false;
  }

  closeNotUpdatedFields(): void {
    this.notUpdatedFieldsVisible = false;
  }

  // Private methods
  private initializeComponent(): void {
    this.setupSubscriptions();
    this.setupInitialSync();
    this.setupPeriodicSync();
  }

  private setupSubscriptions(): void {
    this.subscriptions = [
      this.subscribeToActiveProject(),
      this.subscribeToUIDataStoreWithRelations(),
      this.subscribeToUIDataEvents(),
      this.subscribeToQueryParams(),
      this.subscribeToLoadingStates()
    ];
  }

  private setupInitialSync(): void {
    this.activatedRoute.params.pipe(takeUntil(this.componentDestroyed$)).subscribe(params => {
      const dataStoreId = params['id'];
      if (dataStoreId) {
        this.store.select(state => state.activeProject.data)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe(activeProject => {
            if (activeProject?.id) {
              this.syncRequiredData(activeProject.id);
            }
          });
      }
    });
  }

  private setupPeriodicSync(): void {
    interval(5000).pipe(
      takeUntil(this.componentDestroyed$),
      takeUntil(this.pollingDataStore$)
    ).subscribe(() => {
      if (this.activeProject?.id) {
        this.syncRequiredData(this.activeProject.id);
      }
    });
  }

  private syncRequiredData(projectId: string): void {
    this.syncRemoteDataService.syncRemoteDataItemWithKey(projectId, StoreKeys.UIDataStore);
    this.syncRemoteDataService.syncRemoteDataItemWithKey(projectId, StoreKeys.UIDataEvent);
    this.syncRemoteDataService.syncRemoteDataItemWithKey(projectId, StoreKeys.ProjectDataSchema);
    this.syncRemoteDataService.syncRemoteDataItemWithKey(projectId, StoreKeys.MonitorApplication);
  }

  private cleanup(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
    this.pollingDataStore$.next();
    this.pollingDataStore$.complete();
  }

  private subscribeToActiveProject(): Subscription {
    return this.store.select(state => state.activeProject.data)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(activeProject => {
        this.activeProject = activeProject;
        if (activeProject?.id) {
          this.syncRequiredData(activeProject.id);
        }
        this.setTitle();
      });
  }

  private subscribeToUIDataStoreWithRelations(): Subscription {
    return combineLatest([
      this.store.select(this.createDataSelector()),
      this.activatedRoute.params
    ]).pipe(takeUntil(this.componentDestroyed$)).subscribe(([data, params]) => {
      if (data && params) {
        this.processUIDataStoreData(data, params['id']);
      }
    });
  }

  private createDataSelector() {
    return createSelector(
      state => <any>state['dataSchema'].loading,
      <any>getDataSchemasWithDetailsSelector,
      state => <any[]>state['uiDataStore'].data,
      state => state['uiDataStore'].loading,
      state => state['dataSchema'].notCreatedFields,
      state => state['dataSchema'].notUpdatedFields,
      (dataSchemasLoading, dataSchemas, uiDataStores, uiDataStoresLoading, notCreatedFields, notUpdatedFields) => ({
        dataSchemasLoading,
        dataSchemas,
        uiDataStores,
        uiDataStoresLoading,
        notCreatedFields,
        notUpdatedFields
      })
    );
  }

  private processUIDataStoreData(data: any, currentUiDataStoreId: string): void {
    this.uiDataStoreId = currentUiDataStoreId;

    // Check if we have the required data and it's not loading
    if (!data.uiDataStoresLoading && data.uiDataStores) {
      const uiDataStore = this.findAndProcessUIDataStore(data, currentUiDataStoreId);
      if (uiDataStore) {
        this.updateComponentState(uiDataStore, data);
      }
    }
  }

  private findAndProcessUIDataStore(data: any, currentUiDataStoreId: string): UiDataStoreWithRelations | null {
    const uiDataStore = cloneDeep(data.uiDataStores?.find(dataStore => dataStore.id === currentUiDataStoreId));
    if (uiDataStore) {
      // Find and attach the schema if dataSchemas are available
      if (data.dataSchemas && !data.dataSchemasLoading) {
        uiDataStore.schema = data.dataSchemas?.find(dataSchema =>
          dataSchema.id === uiDataStore.dataSchemaId &&
          dataSchema.category === DataSchemaCategory.UiDataStoreState
        );
      }
      return uiDataStore;
    }
    return null;
  }

  private updateComponentState(uiDataStore: UiDataStoreWithRelations, data: any): void {
    this.uiDataStore = uiDataStore;
    this.updateFieldStates(uiDataStore, data);
    this.setTitle();
    this.updateListConfigs();
  }

  private updateFieldStates(uiDataStore: UiDataStoreWithRelations, data: any): void {
    this.notCreatedFields = data.notCreatedFields.filter(field =>
      field.parentDataSchemaId === uiDataStore.dataSchemaId
    );
    this.notUpdatedFields = data.notUpdatedFields.filter(field =>
      field.parentDataSchemaId === uiDataStore.dataSchemaId
    );

    this.notCreatedFieldsVisible = this.notCreatedFields.length > 0;
    this.notUpdatedFieldsVisible = this.notUpdatedFields.length > 0;
  }

  private updateListConfigs(): void {
    this.updateDataEventListConfig();
    this.updateUIStepFunctionListConfig();
    this.updateUIDataUpdateFunctionListConfig();
  }

  private subscribeToUIDataEvents(): Subscription {
    return combineLatest([
      this.store.select(state => state.uiDataEvent?.data),
      this.activatedRoute.params
    ]).pipe(takeUntil(this.componentDestroyed$)).subscribe(([uiDataEvents, params]) => {
      const currentUiDataStoreId = params['id'];
      this.uiDataEvents = orderBy(
        uiDataEvents?.filter(event => event.uiDataStoreId === currentUiDataStoreId),
        'index',
        'asc'
      );
    });
  }

  private subscribeToQueryParams(): Subscription {
    return this.activatedRoute.queryParams
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(queryParams => {
        if (!Object.keys(queryParams).length) {
          this.router.navigate([], {
            queryParams: QUERY_PARAM_DEFINITIONS.PROJECT.UI_DATA_STORE_DETAIL.FIELDS_TAB
          });
        }
      });
  }

  private subscribeToLoadingStates(): Subscription {
    return combineLatest([
      this.store.select(state => state.uiDataEvent?.loading),
      this.store.select(state => state.uiDataStore?.loading),
      this.store.select(state => state.uiWorkflowStepFunction?.loading),
      this.store.select(state => state.dataSchema.loading),
      this.store.select(state => state.uiDataStore.fieldsLoadingByModelIds)
    ]).pipe(takeUntil(this.componentDestroyed$)).subscribe(([uiDataEventsLoading, uiDataStoresLoading, uiStepFunctionsLoading, dataSchemaLoading, fieldsLoadingByModelIds]) => {
      this.uiDataEventsLoading = uiDataEventsLoading;
      this.uiDataStoresLoading = uiDataStoresLoading;
      this.uiStepFunctionsLoading = uiStepFunctionsLoading;
      this.dataSchemaLoading = dataSchemaLoading;
      this.isFieldsLoading = this.uiDataStore?.projectModelId ?
        fieldsLoadingByModelIds?.some(id => id === this.uiDataStore.projectModelId) || false : false;
    });
  }

  private setTitle(): void {
    const titleItems = [
      {
        label: this.activeProject?.name || 'Loading...',
        redirectUrl: this.activeProject?.id ?
          `${PATH_DEFINITIONS.PROJECTS.PROJECT_DETAIL_PATH}/${this.activeProject.id}` :
          PATH_DEFINITIONS.PROJECTS.PROJECT_DETAIL_PATH,
      },
      {
        label: 'PROJECT_MODULE.UI_DATA_STORE_LIST_COMPONENT.UI_DATA_STORE_LIST',
        redirectUrl: PATH_DEFINITIONS.PROJECTS.UI_DATA_STORE_LIST
      }
    ];

    // Add UI Data Store to breadcrumb if available
    if (this.uiDataStore?.name) {
      titleItems.push({
        label: this.uiDataStore.name,
        redirectUrl: `${PATH_DEFINITIONS.PROJECTS.UI_DATA_STORE_DETAIL}/${this.uiDataStore.id}`
      });
    }

    this.title = titleItems;
  }

  private updateDataEventListConfig(): void {
    this.DATA_EVENT_LIST_CONFIG = this.createListConfig(
      this.DATA_EVENT_LIST_CONFIG,
      ['createDataEvent'],
      ['Edit', 'DELETE_ITEM']
    );
  }

  private updateUIStepFunctionListConfig(): void {
    this.UI_STEP_FUNCTION_LIST_CONFIG = this.createListConfig(
      this.UI_STEP_FUNCTION_LIST_CONFIG,
      ['createUIStepFunction'],
      ['preDataTransformation', 'postDataTransformation', 'edit', 'DELETE_ITEM']
    );
  }

  private updateUIDataUpdateFunctionListConfig(): void {
    this.UI_DATA_UPDATE_FUNCTION_LIST_CONFIG = this.createListConfig(
      this.UI_DATA_UPDATE_FUNCTION_LIST_CONFIG,
      ['createUIDataUpdateFunction'],
      ['editCode', 'addDataMapping', 'DELETE_ITEM']
    );
  }

  private createListConfig(listConfig: any, listActionNamesToFilter: string[], itemActionNamesToFilter: string[]): any {
    return {
      ...listConfig,
      listActions: this.uiDataStore?.isReadOnly ?
        listConfig.listActions.filter(action => !listActionNamesToFilter.includes(action.name)) :
        listConfig.listActions,
      handleVisibilityFn: (data: any, item: any, action: any) => {
        if (this.uiDataStore?.isReadOnly && itemActionNamesToFilter.includes(action.name)) {
          return false;
        }
        if (item.isUpdatable === false && action.name === 'Edit') {
          return false;
        }
        if (item.isDeletable === false && action.name === 'DELETE_ITEM') {
          return false;
        }
        return true;
      }
    };
  }
}
